// Display helpers shared by the conversation list and the thread. Kept together so a timestamp
// reads the same in the sidebar and in the transcript.

const AU_LOCALE = 'en-AU';

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** Whole days between a date and today, ignoring the time of day. */
const daysAgo = (date) => Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000);

/** Compact stamp for a list row: time today, weekday this week, otherwise a short date. */
export const formatListTimestamp = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = daysAgo(date);
  if (days === 0) return date.toLocaleTimeString(AU_LOCALE, { hour: 'numeric', minute: '2-digit' });
  if (days === 1) return 'Yesterday';
  if (days < 7) return date.toLocaleDateString(AU_LOCALE, { weekday: 'short' });
  return date.toLocaleDateString(AU_LOCALE, { day: '2-digit', month: 'short' });
};

/** Time shown under a message bubble. */
export const formatBubbleTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString(AU_LOCALE, { hour: 'numeric', minute: '2-digit' });
};

/** Full stamp for tooltips and screen readers. */
export const formatFullTimestamp = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(AU_LOCALE, { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Label for the divider between days in a transcript. */
export const formatDaySeparator = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = daysAgo(date);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString(AU_LOCALE, { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
};

/** Whether two messages fall on different calendar days (i.e. a separator belongs between them). */
export const isNewDay = (iso, previousIso) => {
  if (!iso) return false;
  if (!previousIso) return true;
  const current = new Date(iso);
  const previous = new Date(previousIso);
  if (Number.isNaN(current.getTime()) || Number.isNaN(previous.getTime())) return false;
  return startOfDay(current) !== startOfDay(previous);
};

/** '+61412345678' shown as '0412 345 678'; anything unexpected is returned untouched. */
export const formatAuPhone = (e164) => {
  const match = /^\+61(\d{9})$/.exec(String(e164 || ''));
  if (!match) return e164 || '';
  const digits = match[1];
  return `0${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

/** Best label for a thread: the franchisee's name, else the number it came from. */
export const conversationTitle = (conversation) =>
  conversation?.franchiseeName || formatAuPhone(conversation?.phoneE164) || 'Unknown number';
